import React, {
  useState,
  useMemo,
  useCallback,
  useRef,
  useEffect,
} from "react";

import "./test.css";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
  ColumnDef,
} from "@tanstack/react-table";
import * as XLSX from "xlsx";
import { Download, Upload, Plus, Trash2, FileJson } from "lucide-react";

// Types
interface CellStyle {
  patternType?: string;
  fgColor?: { rgb: string };
  bgColor?: { rgb: string };
}

interface CellData {
  value: any;
  type?: string;
  format?: string;
  style?: CellStyle;
}

interface JsonDataFormat {
  jsonData?: Record<string, any>[];
  cells?: Record<string, CellData>;
  cellStyles?: Record<string, CellStyle>;
  mergedCells?: any[];
  columnWidths?: number[];
  rowHeights?: number[];
  sheetName?: string;
  totalSheets?: number;
  allSheetNames?: string[];
}

interface TableColumn {
  id: string;
  header: string;
  accessorKey: string;
}

interface TableRow extends Record<string, any> {
  _rowNum: number;
}

interface EditableCellProps {
  value: any;
  rowIndex: number;
  columnId: string;
  updateData: (rowIndex: number, columnId: string, value: any) => void;
  cellStyle?: CellStyle;
}

interface ExcelSpreadsheetProps {
  initialData?: JsonDataFormat | null;
  onDataChange?: (data: TableRow[]) => void;
}

// Utility functions
const columnIndexToLetter = (index: number): string => {
  let letter = "";
  let num = index;
  while (num >= 0) {
    letter = String.fromCharCode(65 + (num % 26)) + letter;
    num = Math.floor(num / 26) - 1;
  }
  return letter;
};

const letterToColumnIndex = (letter: string): number => {
  let index = 0;
  for (let i = 0; i < letter.length; i++) {
    index = index * 26 + (letter.charCodeAt(i) - 64);
  }
  return index - 1;
};

const cellRefToCoords = (ref: string): { col: number; row: number } | null => {
  const match = ref.match(/^([A-Z]+)(\d+)$/);
  if (!match) return null;
  return {
    col: letterToColumnIndex(match[1]),
    row: parseInt(match[2]) - 1,
  };
};

// Custom cell component
const EditableCell: React.FC<EditableCellProps> = ({
  value,
  rowIndex,
  columnId,
  updateData,
  cellStyle,
}) => {
  const [editing, setEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const handleBlur = () => {
    setEditing(false);
    updateData(rowIndex, columnId, localValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      handleBlur();
    } else if (e.key === "Escape") {
      setLocalValue(value);
      setEditing(false);
    }
  };

  const bgColor = cellStyle?.bgColor?.rgb || cellStyle?.fgColor?.rgb;
  const cellBg = bgColor ? `#${bgColor}` : "#ffffff";

  return (
    <div
      className="excel-cell"
      style={{ backgroundColor: cellBg }}
      onDoubleClick={() => setEditing(true)}
    >
      {editing ? (
        <input
          ref={inputRef}
          type="text"
          value={localValue || ""}
          onChange={(e) => setLocalValue(e.target.value)}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          className="excel-cell-input"
          style={{ backgroundColor: cellBg }}
        />
      ) : (
        <span className="excel-cell-content">{value || ""}</span>
      )}
    </div>
  );
};

// Main Spreadsheet Component
const ExcelSpreadsheet: React.FC<ExcelSpreadsheetProps> = ({
  initialData = null,
  onDataChange,
}) => {
  const [data, setData] = useState<TableRow[]>([]);
  const [cellStyles, setCellStyles] = useState<Record<string, CellStyle>>({});
  const [columns, setColumns] = useState<TableColumn[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const jsonInputRef = useRef<HTMLInputElement>(null);

  // Initialize with default data
  useEffect(() => {
    if (initialData) {
      loadFromJson(initialData);
    } else {
      initializeDefaultTable();
    }
  }, []);

  const initializeDefaultTable = () => {
    const defaultCols = 10;
    const defaultRows = 20;

    const cols: TableColumn[] = Array.from({ length: defaultCols }, (_, i) => ({
      id: columnIndexToLetter(i),
      header: columnIndexToLetter(i),
      accessorKey: columnIndexToLetter(i),
    }));

    const rows: TableRow[] = Array.from({ length: defaultRows }, (_, i) => {
      const row: TableRow = { _rowNum: i + 1 };
      cols.forEach((col) => {
        row[col.id] = "";
      });
      return row;
    });

    setColumns(cols);
    setData(rows);
  };

  const loadFromJson = (jsonData: JsonDataFormat) => {
    if (jsonData.cells) {
      // Parse cells format
      const cellMap: Record<number, Record<number, any>> = {};
      const styleMap: Record<string, CellStyle> = {};
      let maxRow = 0;
      let maxCol = 0;

      Object.entries(jsonData.cells).forEach(([ref, cellData]) => {
        const coords = cellRefToCoords(ref);
        if (coords) {
          maxRow = Math.max(maxRow, coords.row);
          maxCol = Math.max(maxCol, coords.col);
          if (!cellMap[coords.row]) cellMap[coords.row] = {};
          cellMap[coords.row][coords.col] = cellData.value;
          if (cellData.style) {
            styleMap[ref] = cellData.style;
          }
        }
      });

      const cols: TableColumn[] = Array.from(
        { length: Math.max(maxCol + 1, 10) },
        (_, i) => ({
          id: columnIndexToLetter(i),
          header: columnIndexToLetter(i),
          accessorKey: columnIndexToLetter(i),
        })
      );

      const rows: TableRow[] = Array.from(
        { length: Math.max(maxRow + 1, 20) },
        (_, i) => {
          const row: TableRow = { _rowNum: i + 1 };
          cols.forEach((col, colIdx) => {
            row[col.id] = cellMap[i]?.[colIdx] || "";
          });
          return row;
        }
      );

      setColumns(cols);
      setData(rows);
      setCellStyles(styleMap);
    } else if (jsonData.jsonData && Array.isArray(jsonData.jsonData)) {
      // Parse jsonData array format
      const keys = Object.keys(jsonData.jsonData[0] || {});
      const cols: TableColumn[] = keys.map((key) => ({
        id: key,
        header: key,
        accessorKey: key,
      }));

      setColumns(cols);
      setData(jsonData.jsonData.map((row, i) => ({ ...row, _rowNum: i + 1 })));
    }
  };

  const updateData = useCallback(
    (rowIndex: number, columnId: string, value: any) => {
      setData((old) =>
        old.map((row, index) => {
          if (index === rowIndex) {
            return { ...row, [columnId]: value };
          }
          return row;
        })
      );
      onDataChange?.(data);
    },
    [data, onDataChange]
  );

  const tableColumns = useMemo<ColumnDef<TableRow, any>[]>(() => {
    const columnHelper = createColumnHelper<TableRow>();

    return [
      columnHelper.display({
        id: "_rowNumber",
        header: "",
        cell: ({ row }) => (
          <div className="excel-row-header">{row.index + 1}</div>
        ),
        size: 40,
      }),
      ...columns.map((col) =>
        columnHelper.accessor(col.accessorKey as any, {
          id: col.id,
          header: col.header,
          cell: ({ getValue, row, column }) => {
            const cellRef = `${col.id}${row.index + 1}`;
            return (
              <EditableCell
                value={getValue()}
                rowIndex={row.index}
                columnId={column.id}
                updateData={updateData}
                cellStyle={cellStyles[cellRef]}
              />
            );
          },
          size: 120,
        })
      ),
    ];
  }, [columns, updateData, cellStyles]);

  const table = useReactTable({
    data,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const addRow = () => {
    const newRow: TableRow = { _rowNum: data.length + 1 };
    columns.forEach((col) => {
      newRow[col.id] = "";
    });
    setData([...data, newRow]);
  };

  const addColumn = () => {
    const newColIndex = columns.length;
    const newColId = columnIndexToLetter(newColIndex);
    setColumns([
      ...columns,
      {
        id: newColId,
        header: newColId,
        accessorKey: newColId,
      },
    ]);
    setData(data.map((row) => ({ ...row, [newColId]: "" })));
  };

  const deleteRow = () => {
    if (data.length > 1) {
      setData(data.slice(0, -1));
    }
  };

  const deleteColumn = () => {
    if (columns.length > 1) {
      const lastCol = columns[columns.length - 1];
      setColumns(columns.slice(0, -1));
      setData(
        data.map((row) => {
          const { [lastCol.id]: _, ...rest } = row;
          return rest as TableRow;
        })
      );
    }
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(
      data.map((row) => {
        const { _rowNum, ...rest } = row;
        return rest;
      })
    );
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    XLSX.writeFile(wb, "spreadsheet.xlsx");
  };

  const exportToJson = () => {
    const exportData: JsonDataFormat = {
      jsonData: data.map((row) => {
        const { _rowNum, ...rest } = row;
        return rest;
      }),
      cells: {},
      cellStyles,
      sheetName: "Sheet1",
    };

    data.forEach((row, rowIdx) => {
      columns.forEach((col) => {
        const cellRef = `${col.id}${rowIdx + 1}`;
        const value = row[col.id];
        if (value !== "" || cellStyles[cellRef]) {
          exportData.cells![cellRef] = {
            value: value,
            type: "s",
            format: "General",
            style: cellStyles[cellRef] || {
              patternType: "solid",
              fgColor: { rgb: "FFFFFF" },
              bgColor: { rgb: "FFFFFF" },
            },
          };
        }
      });
    });

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "spreadsheet.json";
    a.click();
    URL.revokeObjectURL(url);
  };

  const importExcel = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target?.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const importedData: any[] = XLSX.utils.sheet_to_json(ws);

      if (importedData.length > 0) {
        const keys = Object.keys(importedData[0]);
        const cols: TableColumn[] = keys.map((key) => ({
          id: key,
          header: key,
          accessorKey: key,
        }));

        setColumns(cols);
        setData(importedData.map((row, i) => ({ ...row, _rowNum: i + 1 })));
      }
    };
    reader.readAsBinaryString(file);
  };

  const importJson = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const jsonData: JsonDataFormat = JSON.parse(
          evt.target?.result as string
        );
        loadFromJson(jsonData);
      } catch (error) {
        console.error("Error parsing JSON:", error);
        alert("Invalid JSON file");
      }
    };
    reader.readAsText(file);
  };

  return (
    <div className="excel-container">
      <div className="excel-toolbar">
        <button onClick={addRow} className="toolbar-btn" title="Add Row">
          <Plus size={16} /> Row
        </button>
        <button onClick={addColumn} className="toolbar-btn" title="Add Column">
          <Plus size={16} /> Column
        </button>
        <button
          onClick={deleteRow}
          className="toolbar-btn"
          title="Delete Last Row"
        >
          <Trash2 size={16} /> Row
        </button>
        <button
          onClick={deleteColumn}
          className="toolbar-btn"
          title="Delete Last Column"
        >
          <Trash2 size={16} /> Column
        </button>
        <div className="toolbar-divider" />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="toolbar-btn"
        >
          <Upload size={16} /> Import Excel
        </button>
        <button
          onClick={() => jsonInputRef.current?.click()}
          className="toolbar-btn"
        >
          <FileJson size={16} /> Import JSON
        </button>
        <button onClick={exportToExcel} className="toolbar-btn">
          <Download size={16} /> Export Excel
        </button>
        <button onClick={exportToJson} className="toolbar-btn">
          <Download size={16} /> Export JSON
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls"
          onChange={importExcel}
          style={{ display: "none" }}
        />
        <input
          ref={jsonInputRef}
          type="file"
          accept=".json"
          onChange={importJson}
          style={{ display: "none" }}
        />
      </div>

      <div className="excel-table-container">
        <table className="excel-table">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ width: header.getSize() }}
                    className={
                      header.id === "_rowNumber"
                        ? "excel-corner"
                        : "excel-header"
                    }
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td
                    key={cell.id}
                    className={
                      cell.column.id === "_rowNumber" ? "excel-row-number" : ""
                    }
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default function Test() {
  const sampleData: JsonDataFormat = {
    jsonData: [
      { a1: "a2", b1: "b2", c1: "c2", d1: "d2" },
      { a1: "a3", b1: "b3", c1: "c3", d1: "d3" },
      { a1: "a4", b1: "b4", c1: "c4", d1: "d4" },
    ],
    cells: {
      A1: {
        value: "a1",
        type: "s",
        format: "General",
        style: {
          patternType: "solid",
          fgColor: { rgb: "FFFF00" },
          bgColor: { rgb: "FFFF00" },
        },
      },
      B1: {
        value: "b1",
        type: "s",
        format: "General",
        style: {
          patternType: "solid",
          fgColor: { rgb: "FFFF00" },
          bgColor: { rgb: "FFFF00" },
        },
      },
    },
  };

  return (
    <ExcelSpreadsheet
      initialData={sampleData}
      onDataChange={(data) => console.log("Data changed:", data)}
    />
  );
}
